/**
 * Syncs announcement bar with header sticky reveal (page-header.js).
 * Uses --wt-announce-placeholder + .wt-announcement__flow-spacer to avoid layout jump
 * when the bar becomes position:fixed.
 */
(() => {
  const SESSION_KEY = "wt-announcement-dismissed-session";

  function getAnnouncementSection() {
    return document.querySelector(".shopify-section.wt-announcement");
  }

  function getAnnouncementBar() {
    const ann = getAnnouncementSection();
    return ann?.querySelector("announcement-bar.wt-announcement-bar") ?? null;
  }

  function getPageHeader() {
    return document.querySelector("header.page-header");
  }

  function setHeaderHeightVar(header) {
    if (!header) return;
    const h = Math.round(header.getBoundingClientRect().height);
    document.documentElement.style.setProperty(
      "--wt-page-header-height",
      `${h}px`,
    );
  }

  function setPlaceholderHeight(ann, bar) {
    if (!ann || !bar) return;
    const h = Math.round(bar.getBoundingClientRect().height);
    ann.style.setProperty("--wt-announce-placeholder", `${h}px`);
  }

  function clearPlaceholder(ann) {
    ann?.style?.removeProperty?.("--wt-announce-placeholder");
  }

  function updatePinnedLayout() {
    if (!document.body.classList.contains("wt-announce-below-sticky")) return;
    const ann = getAnnouncementSection();
    const header = getPageHeader();
    const bar = getAnnouncementBar();
    if (header) setHeaderHeightVar(header);
    if (ann && bar) setPlaceholderHeight(ann, bar);
  }

  function sync() {
    const ann = getAnnouncementSection();
    const header = getPageHeader();
    if (!ann || !header || ann.style.display === "none") {
      document.body.classList.remove("wt-announce-below-sticky");
      clearPlaceholder(ann);
      return;
    }

    const enabled = header.classList.contains("sticky-enabled");
    const shown = header.classList.contains("sticky-show");
    const pinBelow = enabled && shown;
    const wasPinned = document.body.classList.contains("wt-announce-below-sticky");
    const bar = getAnnouncementBar();

    if (pinBelow) {
      if (!wasPinned && bar) {
        setPlaceholderHeight(ann, bar);
      }
      setHeaderHeightVar(header);
      document.body.classList.add("wt-announce-below-sticky");
    } else {
      document.body.classList.remove("wt-announce-below-sticky");
      clearPlaceholder(ann);
    }
  }

  let raf = 0;
  function scheduleSync() {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      sync();
    });
  }

  let headerObserver = null;
  let windowListenersBound = false;

  function bindHeader(header) {
    if (!header) return;
    if (headerObserver) headerObserver.disconnect();
    headerObserver = new MutationObserver(scheduleSync);
    headerObserver.observe(header, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  function onWindowResize() {
    scheduleSync();
    updatePinnedLayout();
  }

  function init() {
    const ann = getAnnouncementSection();
    const header = getPageHeader();
    if (!ann || !header) return;

    if (sessionStorage.getItem(SESSION_KEY) === "1") {
      ann.style.display = "none";
      document.body.classList.remove("wt-announce-below-sticky");
      clearPlaceholder(ann);
      return;
    }

    bindHeader(header);
    if (!windowListenersBound) {
      windowListenersBound = true;
      window.addEventListener("scroll", scheduleSync, { passive: true });
      window.addEventListener("resize", onWindowResize, { passive: true });
    }
    scheduleSync();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (!event.target?.classList?.contains("wt-announcement")) return;
    init();
  });
})();
